import { useEffect, useState } from 'react'
import type { SessionInfo } from '../types'

interface Props {
  session: SessionInfo
  // Optional pause/resume controls — facilitator only.
  onPause?: () => void
  onResume?: () => void
  // Optional ±30s adjust — facilitator only. Called with seconds (e.g. +30, -30).
  onAdjust?: (deltaSeconds: number) => void
  pending?: boolean
}

/**
 * Countdown display for an auto-advancing session. Computes remaining time
 * from server-supplied `quarter_started_at` and `server_now`, so the clock
 * survives browser refresh and is robust to client/server clock skew.
 *
 * On a paused session, shows the frozen remainder; once unpaused, ticks
 * down. When `minutes_per_quarter` is null/0, the component renders nothing.
 */
export default function QuarterClock({
  session, onPause, onResume, onAdjust, pending,
}: Props) {
  const tick = useTick(1000)
  const remaining = computeRemaining(session, tick)
  const canShorten = remaining >= 30

  if (session.minutes_per_quarter == null || session.minutes_per_quarter <= 0) {
    return null
  }
  if (session.status !== 'active') return null

  const paused = session.paused_remaining_seconds != null
  const display = formatMMSS(Math.max(0, remaining))
  const tone = remaining <= 10 ? 'danger'
    : remaining <= 30 ? 'warning' : 'ok'
  const toneClasses = paused
    ? 'bg-slate-100 text-slate-600 border-slate-300'
    : tone === 'danger'
      ? 'bg-red-100 text-red-700 border-red-200'
      : tone === 'warning'
        ? 'bg-amber-100 text-amber-800 border-amber-200'
        : 'bg-emerald-50 text-emerald-700 border-emerald-200'

  const adjustBtnClass = 'h-7 w-7 rounded border border-slate-300 bg-white '
    + 'text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed '
    + 'text-sm font-semibold leading-none flex items-center justify-center'

  return (
    <div className="flex items-center gap-2">
      {onAdjust && (
        <button
          type="button"
          className={adjustBtnClass}
          aria-label="Shorten this quarter by 30 seconds"
          title="−30s"
          disabled={pending || !canShorten}
          onClick={() => onAdjust(-30)}
        >
          −
        </button>
      )}
      <div
        className={`flex items-baseline gap-1 rounded border px-3 py-1 font-mono tabular-nums ${toneClasses}`}
        title={paused ? 'Quarter clock paused' : 'Time left in this quarter'}
        aria-label={paused ? 'Quarter clock paused' : 'Time left in this quarter'}
      >
        <span className="text-lg font-semibold leading-none">{display}</span>
        {paused && <span className="text-xs uppercase tracking-wide">paused</span>}
      </div>
      {onAdjust && (
        <button
          type="button"
          className={adjustBtnClass}
          aria-label="Extend this quarter by 30 seconds"
          title="+30s"
          disabled={pending}
          onClick={() => onAdjust(30)}
        >
          +
        </button>
      )}
      {onPause && onResume && (
        <button
          type="button"
          className="text-xs text-slate-500 hover:text-brand-700 underline-offset-2 hover:underline"
          disabled={pending}
          onClick={paused ? onResume : onPause}
        >
          {paused ? 'Resume' : 'Pause'}
        </button>
      )}
    </div>
  )
}

/** Returns a value that changes once per `interval` ms — used purely as a
 * trigger to re-render the countdown; the actual remaining-seconds math
 * derives from server-supplied timestamps, not from this counter.
 *
 * Also force-ticks on visibilitychange so the display snaps to the right
 * value the instant the tab returns to the foreground — browsers throttle
 * setInterval in background tabs and the next natural tick could be up to
 * `interval` ms away. */
function useTick(interval: number): number {
  const [n, setN] = useState(0)
  useEffect(() => {
    const bump = () => setN((x) => x + 1)
    const id = window.setInterval(bump, interval)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') bump()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [interval])
  return n
}

/** Compute remaining seconds. Uses the offset between server_now (snapshot
 * time) and the client clock at that snapshot to neutralize clock skew. */
function computeRemaining(s: SessionInfo, _tick: number): number {
  if (s.minutes_per_quarter == null || s.minutes_per_quarter <= 0) return 0
  if (s.paused_remaining_seconds != null) return s.paused_remaining_seconds
  if (s.quarter_started_at == null) return s.minutes_per_quarter * 60
  // server_now was captured when the response was generated; subtracting it
  // from quarter_started_at gives "elapsed when server measured." We then
  // add the client-side delta since first observing server_now.
  const offset = serverOffsetSeconds(s.server_now)
  const nowOnServer = Date.now() / 1000 - offset
  const elapsed = nowOnServer - s.quarter_started_at
  return s.minutes_per_quarter * 60 - elapsed
}

/** Cached client→server clock offset, pinned to the `server_now` value it
 * was derived from. Recomputed only when a genuinely new `server_now`
 * reading arrives — otherwise an old reading combined with the current
 * client clock would smuggle the elapsed-since-fetch time into the offset,
 * which is exactly what makes the countdown go out of sync after a long
 * tab-hide (the natural-refetch interval grows during the hide, so the
 * elapsed gap can be tens of seconds). */
let _cachedOffset: number | null = null
let _offsetSourceServerNow: number | null = null

function serverOffsetSeconds(serverNow: number | undefined): number {
  if (!serverNow) return _cachedOffset ?? 0
  if (_offsetSourceServerNow !== serverNow) {
    _cachedOffset = Date.now() / 1000 - serverNow
    _offsetSourceServerNow = serverNow
  }
  return _cachedOffset ?? 0
}

function formatMMSS(seconds: number): string {
  const total = Math.ceil(seconds)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}
